import React, { useState, useContext, useMemo } from "react"
import {
    Button,
    IconButton,
    componentStyles,
    useTheme,
    useColors,
    useToastWithActionWithDeferredProps,
} from "@mallardbay/lib-react-components"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faHeart as faHeartOutline } from "@fortawesome/free-regular-svg-icons/faHeart"
import { faHeart } from "@fortawesome/free-solid-svg-icons/faHeart"

import { UserContext } from "~config/user-provider"
import WishlistModal from "~components/pages/wishlist/wishlist-modal"
import type { WishlistFieldsFragment } from "~graphql/generated/graphql"
import { useAuth } from "~utils/auth-hooks"
import { navigateToWishlist } from "~utils/navigation-helpers"
import COPY from "~config/copy-constants"
import { TEST_IDS } from "~config/test-ids"
import useWishlistsByUser from "~utils/use-wishlists-by-user"
import { useUpdateWishlist } from "~utils/use-update-wishlist"
import useDisclosure from "~components/shared/todo-lib-react-components/use-disclosure"

interface Props {
    readonly listingId: string
    readonly isSaved?: boolean
    readonly isHeader?: boolean
    readonly isInTitle?: boolean
}

export default function WishlistButton({
    listingId,
    isSaved: initialIsSaved,
    isHeader,
    isInTitle,
}: Props) {
    const { isOpen, onOpen, onClose } = useDisclosure()
    const { isAuthenticated, loginWithRedirect } = useAuth()
    const { user, isFetching } = useContext(UserContext)
    const {
        isSaved,
        isLoading,
        wishlistIndex,
        wishlists,
        associatedWishlistId,
    } = useWishlistsData({ listingId, initialIsSaved, userId: user?.id })
    const buttonLabel = isSaved ? COPY.SAVED : COPY.SAVE
    const [isCreating, setIsCreating] = useState(false)

    const styles = useStyles({ isHeader })

    const icon = isSaved ? faHeart : faHeartOutline
    const iconStyle = isSaved ? styles.iconFill : {}

    const showToast = useToastWithActionWithDeferredProps()
    const { updateWishlist, isLoading: isUpdating } = useUpdateWishlist({
        onCompleted: (updatedWishlist) => {
            showToast({
                message: `${COPY.REMOVED_FROM} ${updatedWishlist.name}`,
                actionLabel: COPY.VIEW_LIST,
                onActionClick: () => navigateToWishlist(updatedWishlist.id),
                icon: faHeartOutline,
            })
            onClose()
        },
    })

    const handleButtonClick = () => {
        if (isSaved && associatedWishlistId) {
            void updateWishlist(associatedWishlistId, {
                listings: getNewListingsArray({
                    listingId,
                    wishlistIndex,
                    wishlists,
                }),
            })
        } else if (isAuthenticated) {
            onOpen()
        } else {
            void loginWithRedirect()
        }
    }

    return (
        <>
            {isInTitle ? (
                <Button
                    testId={TEST_IDS.WISHLIST_BUTTON}
                    leftIcon={<FontAwesomeIcon icon={icon} style={iconStyle} />}
                    isLoading={isUpdating || isFetching || isLoading}
                    style={styles.titleButton}
                    onClick={handleButtonClick}
                    variant="ghost"
                >
                    {buttonLabel}
                </Button>
            ) : (
                <IconButton
                    ariaLabel={COPY.SAVE}
                    testId={TEST_IDS.WISHLIST_ICON_BUTTON}
                    style={styles.button}
                    onClick={handleButtonClick}
                    isDisabled={isUpdating || isFetching || isLoading}
                    icon={<FontAwesomeIcon icon={icon} style={iconStyle} />}
                />
            )}
            {user && (
                <WishlistModal
                    listingId={listingId}
                    wishlists={wishlists}
                    user={user}
                    isFetching={isFetching}
                    isOpen={isOpen}
                    onClose={onClose}
                    isCreating={isCreating}
                    setIsCreating={setIsCreating}
                />
            )}
        </>
    )
}

function useStyles({ isHeader }: { isHeader?: boolean }) {
    const colors = useColors()
    const theme = useTheme()

    return componentStyles({
        button: {
            boxShadow: "md",
            marginLeft: 2,
            backgroundColor: colors.bg,
            opacity: 0.85,
            ...(isHeader && {
                zIndex: theme.zIndices.modal,
                opacity: 1,
            }),
            _hover: {
                backgroundColor: colors.bgSecondary,
            },
        },
        iconFill: {
            color: colors.error,
        },
        titleButton: {
            marginLeft: "8px",
            borderRadius: "full",
            fontSize: "15px",
        },
        root: {
            backgroundColor: colors.bg,
        },
        secondary: {
            backgroundColor: colors.bgSecondary,
        },
        error: {
            color: colors.error,
        },
    })
}

function useWishlistsData({
    initialIsSaved,
    userId,
    listingId,
}: {
    initialIsSaved?: boolean
    userId?: string
    listingId: string
}) {
    const { wishlists, isLoading } = useWishlistsByUser({
        userId,
    })

    return useMemo(() => {
        let isSaved = initialIsSaved ?? false
        let wishlistIndex = -1
        wishlists.every((wishlist, index) => {
            if (wishlist.listings.find((listing) => listing.id === listingId)) {
                isSaved = true
                wishlistIndex = index
                return false
            }
            return true
        })
        const associatedWishlistId = wishlists[wishlistIndex]?.id

        return {
            associatedWishlistId,
            wishlists,
            isLoading,
            isSaved,
            wishlistIndex,
        }
    }, [initialIsSaved, userId, listingId, wishlists])
}

function getNewListingsArray({
    wishlistIndex,
    wishlists,
    listingId,
}: {
    wishlistIndex: number
    wishlists: WishlistFieldsFragment[]
    listingId: string
}) {
    if (wishlistIndex < 0) return []

    const wishlistListings = wishlists[wishlistIndex]?.listings ?? []
    const listingIdArray = [...wishlistListings.map((listing) => listing.id)]
    listingIdArray.splice(listingIdArray.indexOf(listingId), 1)
    return listingIdArray
}
